/**
 * Pull a human-readable message out of an Axios error from the Pixie API.
 *
 * Backend conventions:
 * - String errors: `{ success: false, error: "Failed to send login link." }`
 * - DRF field errors: `{ success: false, error: { email: ["Enter a valid email address."] } }`
 * - Plain DRF: `{ detail: "Authentication credentials were not provided." }`
 *
 * Falls back to the supplied default when none of the above are present.
 */
export function extractApiError(err: unknown, fallback: string): string {
  // Axios shape: err.response.data.{error,detail}
  const data = (err as { response?: { data?: unknown } } | null)?.response?.data

  if (data && typeof data === 'object') {
    const payload = data as Record<string, unknown>

    // String error from PixieWorld views.
    if (typeof payload.error === 'string' && payload.error.trim()) {
      return payload.error
    }

    // DRF "detail" string.
    if (typeof payload.detail === 'string' && payload.detail.trim()) {
      return payload.detail
    }

    // Field-level validation errors. DRF returns either an object keyed by
    // field name with arrays of messages, or a flat array. Prefer the first
    // readable message we find.
    const candidate = (typeof payload.error === 'object' && payload.error)
      || (typeof payload.errors === 'object' && payload.errors)
      || payload

    if (candidate && typeof candidate === 'object') {
      for (const value of Object.values(candidate as Record<string, unknown>)) {
        if (typeof value === 'string' && value.trim()) return value

        if (Array.isArray(value)) {
          const first = value.find((v) => typeof v === 'string' && v.trim())

          if (typeof first === 'string') return first
        }
      }
    }
  }

  if (err instanceof Error && err.message && !err.message.startsWith('Request failed with status')) {
    return err.message
  }

  return fallback
}
